// Directory repository: manifest refresh and number resolution.
//
// The resolution contract of this file:
//
// 1. LOCAL ROWS FIRST. An entry fetched within the last half hour
//    (config.directoryEntryMaxAgeMs) answers without any network — this is the
//    throttle that keeps routine conversation opens from becoming a live query
//    per contact.
// 2. A GATED ENTRY LOOKUP when the local copy is stale or missing. There are no
//    published sections and no full snapshot any more: resolution is one question
//    per number, asked at the entry gate, which requires a device signature and
//    counts every ask. The response must carry an inclusion proof to a
//    threshold-signed root — an unproven entry is unbacked data — and it is never
//    keyed by the phone number, only by the derived entry key.
// 3. A STALE ROW OVER AN ERROR. A gate that is unreachable or refusing on budget
//    must not orphan an existing contact; only a client with nothing at all
//    surfaces the failure.
//
// The directory operator therefore sees which entry keys this device asks about.
// That is a stated property of the design, not a leak: what the gate exists to stop
// is MASS checking — offline enumeration of published files, invisible and
// unstoppable — not the single ask, which the wake gateway's metadata already
// implies. A cache without refresh would still be a mistake in the other direction:
// if the client remembered mappings indefinitely, no directory change — a
// withdrawal included — would ever reach anyone who already added that contact.

import { config } from '$lib/config';
import { deriveEntryKey, EntryKey } from '$lib/crypto/entry-key';
import { verifyEntryProof } from '$lib/crypto/entry-proof';
import { fingerprintOfKey } from '$lib/crypto/hash';
import { verifyManifest } from '$lib/crypto/manifest-verifier';
import { db, type ManifestRow } from '$lib/db/db';

import { fetchEntryByKey, fetchManifest } from '$lib/protocol/directory-api';
import { ClientError, type ErrorCode } from '$lib/protocol/errors';
import {
  parseDirectoryEntry,
  parseEntryLookupResult,
  parseManifestContent,
  type DirectoryEntry,
  type LookupParams,
  type ManifestContent,
} from '$lib/protocol/types';

/** Derived keys are worth caching: the derivation is slow on purpose, and the
 *  same number is resolved on every sync pass. Keyed by salt as well, so a
 *  republish with a new salt can never serve a stale key. */
const keyCache = new Map<string, EntryKey>();

/** Last refresh failure, for the diagnostics screen. */
let lastRefreshError: ErrorCode | undefined;

export interface DirectoryStatus {
  manifest: ManifestContent | undefined;
  fetchedAtMs: number | undefined;
  fromDomain: string | undefined;
  cachedEntries: number;
  lastError: ErrorCode | undefined;
}

async function manifestRow(): Promise<ManifestRow | undefined> {
  return db.manifest.get(1);
}

export async function loadManifest(): Promise<ManifestContent | undefined> {
  const row = await manifestRow();
  if (row === undefined) {
    return undefined;
  }
  try {
    return parseManifestContent(JSON.parse(row.bodyJson) as Record<string, unknown>);
  } catch {
    return undefined;
  }
}

export async function directoryStatus(): Promise<DirectoryStatus> {
  const row = await manifestRow();
  return {
    manifest: await loadManifest(),
    fetchedAtMs: row?.fetchedAtMs,
    fromDomain: row?.fromDomain,
    cachedEntries: await db.directoryEntries.count(),
    lastError: lastRefreshError,
  };
}

/** The URLs the client talks to. After the first successful fetch, the list
 *  inside the manifest wins — which is why losing a domain is a data change
 *  rather than a security incident: publish a manifest without it and everyone
 *  stops using it, with no app update and no action from the user. */
export async function directoryUrls(): Promise<string[]> {
  const m = await loadManifest();
  if (m !== undefined && m.directoryDomains.length > 0) {
    return m.directoryDomains;
  }
  return config.directoryUrls;
}

/** Keys allowed to sign the incoming manifest: those from the previous manifest
 *  plus its set of future keys — what lets a signing key rotate without an app
 *  update. On first launch the compiled-in list remains. Bootstrap entries may be
 *  `fingerprint:key` or the bare key, in which case the fingerprint is computed —
 *  manifest signatures reference keys by fingerprint. */
async function allowedKeys(previous: ManifestRow | undefined): Promise<Map<string, string>> {
  if (previous !== undefined) {
    try {
      const m = parseManifestContent(JSON.parse(previous.bodyJson) as Record<string, unknown>);
      if (m !== undefined && Object.keys(m.keys).length > 0) {
        return new Map<string, string>([...Object.entries(m.keys), ...Object.entries(m.futureKeys)]);
      }
    } catch {
      // Fall through to bootstrap.
    }
  }
  const out = new Map<string, string>();
  for (const entry of config.bootstrapKeys) {
    const colon = entry.indexOf(':');
    if (colon > 0) {
      out.set(entry.slice(0, colon), entry.slice(colon + 1));
    } else {
      out.set(await fingerprintOfKey(entry), entry);
    }
  }
  return out;
}

async function previousThreshold(previous: ManifestRow | undefined): Promise<number> {
  if (previous !== undefined) {
    try {
      const m = parseManifestContent(JSON.parse(previous.bodyJson) as Record<string, unknown>);
      if (m !== undefined) {
        return m.threshold;
      }
    } catch {
      // Fall through.
    }
  }
  return config.bootstrapThreshold;
}

/** Fetches and verifies the manifest. Returns undefined when nothing needed doing. */
export async function refreshDirectory(options?: {
  force?: boolean;
  nowMs?: number;
}): Promise<ManifestContent | undefined> {
  const nowMs = options?.nowMs ?? Date.now();
  const previous = await manifestRow();

  if (options?.force !== true && previous !== undefined) {
    const sinceLast = nowMs - previous.fetchedAtMs;
    const valid = nowMs < previous.validUntilMs;
    if (valid && sinceLast < config.directoryRefreshMs) {
      return undefined;
    }
  }

  const urls = await directoryUrls();
  if (urls.length === 0) {
    throw new ClientError('directory_not_configured');
  }

  try {
    const fetched = await fetchManifest(urls);
    const check = await verifyManifest(fetched.manifest, {
      allowedKeys: await allowedKeys(previous),
      threshold: await previousThreshold(previous),
      knownVersion: previous?.version ?? 0,
      now: new Date(nowMs),
      clockToleranceS: config.defaultClockToleranceS,
      expectedProtocolVersion: config.protocolVersion,
    });
    if (!check.accepted) {
      throw new ClientError(check.error);
    }
    const content = parseManifestContent(check.body);
    if (content === undefined) {
      throw new ClientError('unreadable_response');
    }
    await db.manifest.put({
      id: 1,
      bodyJson: JSON.stringify(check.body),
      version: content.version,
      validUntilMs: content.validUntil.getTime(),
      fetchedAtMs: nowMs,
      fromDomain: fetched.domain,
    });
    lastRefreshError = undefined;
    return content;
  } catch (error) {
    lastRefreshError = error instanceof ClientError ? error.code : 'unknown';
    throw error;
  }
}

function signingKeys(m: ManifestContent): Map<string, string> {
  return new Map<string, string>([...Object.entries(m.keys), ...Object.entries(m.futureKeys)]);
}

async function keyForPhone(params: LookupParams, phone: string): Promise<EntryKey> {
  const cacheKey = `${params.saltB64}|${phone}`;
  const cached = keyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  const key = await deriveEntryKey(phone, params);
  if (keyCache.size > 256) {
    const first = keyCache.keys().next();
    if (!first.done) {
      keyCache.delete(first.value);
    }
  }
  keyCache.set(cacheKey, key);
  return key;
}

/** Looks a company up by number, local rows first. */
export async function lookupNumber(phone: string): Promise<DirectoryEntry | undefined> {
  let refreshError: ClientError | undefined;
  try {
    await refreshDirectory();
  } catch (error) {
    // A stale directory still resolves numbers; only a client with nothing at all
    // has to surface the failure.
    refreshError = error instanceof ClientError ? error : new ClientError('unknown');
  }

  const m = await loadManifest();
  if (m === undefined) {
    throw refreshError ?? new ClientError('directory_not_configured');
  }

  const params = m.lookup;
  if (params === undefined) {
    // Without lookup parameters the directory publishes nothing this client could
    // resolve a number against.
    throw new ClientError('unreadable_response');
  }
  return resolve(m, await keyForPhone(params, phone), phone);
}

/** [lookupNumber], following a signed `moved` chain to the successor. The user
 *  sees the state; they are not asked to confirm anything, because their consent
 *  verifies nothing — the predecessor's signature does. */
export async function lookupFollowingMoves(phone: string): Promise<DirectoryEntry | undefined> {
  let entry = await lookupNumber(phone);
  for (
    let hop = 0;
    hop < 3 &&
    entry !== undefined &&
    entry.status === 'moved' &&
    entry.successorSigned &&
    entry.successorKey !== undefined;
    hop++
  ) {
    // Followed by key, because that is how the entry names the successor. This
    // client never learns their number and does not need to.
    const successor = await lookupByKey(entry.successorKey);
    if (successor === undefined) {
      break;
    }
    entry = successor;
  }
  return entry;
}

/** An entry by its key rather than by a number — the path a signed move takes. */
export async function lookupByKey(entryKeyB64: string): Promise<DirectoryEntry | undefined> {
  const m = await loadManifest();
  if (m === undefined) {
    return undefined;
  }
  return resolve(m, EntryKey.fromBase64(entryKeyB64));
}

/** An entry from the locally held rows, without touching the network. */
export async function entryFromLocal(phone: string): Promise<DirectoryEntry | undefined> {
  const params = (await loadManifest())?.lookup;
  if (params === undefined) {
    return undefined;
  }
  const key = await keyForPhone(params, phone);
  return localByKey(key.base64, phone);
}

/** Companies among a set of phone numbers, resolved WITHOUT disclosing them.
 *
 *  Reads ONLY the locally held rows — entries this client fetched before.
 *  Deliberately never asks the gate: a query per picked contact would hand the
 *  operator the address book in outline and burn the device's lookup budget doing
 *  it. With the bulk snapshot gone this matches far less than it used to; the
 *  address-book feature that depended on the snapshot is parked, not redesigned
 *  here.
 *
 *  Callers treat this as a background job — every number costs a slow scrypt
 *  derivation on purpose. */
export async function entriesForNumbers(numbers: string[]): Promise<DirectoryEntry[]> {
  const params = (await loadManifest())?.lookup;
  if (params === undefined) {
    return [];
  }
  const out: DirectoryEntry[] = [];
  for (const phone of new Set(numbers)) {
    const key = await keyForPhone(params, phone);
    const entry = await localByKey(key.base64, phone);
    if (entry !== undefined) {
      out.push(entry);
    }
  }
  return out;
}

async function localByKey(entryKeyB64: string, phone?: string): Promise<DirectoryEntry | undefined> {
  const row = await db.directoryEntries.get(entryKeyB64);
  if (row === undefined) {
    return undefined;
  }
  try {
    const json = JSON.parse(row.rawJson) as Record<string, unknown>;
    return parseDirectoryEntry(json, phone ?? row.phone ?? undefined);
  } catch {
    return undefined;
  }
}

/** Local row if fresh; the gated lookup otherwise; a stale row over an error. */
async function resolve(
  m: ManifestContent,
  key: EntryKey,
  phone?: string,
): Promise<DirectoryEntry | undefined> {
  const row = await db.directoryEntries.get(key.base64);
  if (row !== undefined && Date.now() - row.updatedAtMs < config.directoryEntryMaxAgeMs) {
    return localByKey(key.base64, phone);
  }

  try {
    const fetched = await entryLookup(m, key, phone);
    if (fetched === undefined) {
      // A 404 from the gate is definitive: nothing is published under this key any
      // more. (A withdrawn entry keeps its file, with that status — this branch is
      // only for keys that left the directory entirely.)
      if (row !== undefined) {
        await db.directoryEntries.delete(key.base64);
      }
      return undefined;
    }
    await saveRow(fetched);
    return fetched;
  } catch (error) {
    const stale = await localByKey(key.base64, phone);
    if (stale !== undefined) {
      return stale;
    }
    throw error;
  }
}

async function entryLookup(
  m: ManifestContent,
  key: EntryKey,
  phone?: string,
): Promise<DirectoryEntry | undefined> {
  const raw = await fetchEntryByKey(await directoryUrls(), key.base64);
  if (raw === null) {
    return undefined;
  }
  const result = parseEntryLookupResult(raw);
  if (result === undefined) {
    throw new ClientError('unreadable_response');
  }
  const proved = await verifyEntryProof(
    {
      rawEntry: result.rawEntry,
      proofRootB64: result.proofRootB64,
      proofPathB64: result.proofPathB64,
      snapshotVersion: result.snapshotVersion,
      signatures: result.signatures,
    },
    key.bytes,
    signingKeys(m),
    m.threshold,
  );
  // The proof binds the entry to the signed set — and the key check binds it to
  // the question that was asked. Without the latter, a directory could answer any
  // key with any correctly proven entry.
  if (!proved || result.entry.entryKey !== key.base64) {
    throw new ClientError('entry_proof_invalid');
  }
  // The number never came from the server; it is what this client hashed to get here.
  return { ...result.entry, phone: phone ?? result.entry.phone };
}

async function saveRow(e: DirectoryEntry): Promise<void> {
  await db.directoryEntries.put({
    entryKey: e.entryKey,
    // The column survives from the section era; entries fetched through the gate
    // all live under the empty prefix.
    sectionPrefix: '',
    rawJson: JSON.stringify(entryToStorageJson(e)),
    phone: e.phone ?? null,
    updatedAtMs: Date.now(),
  });
}

/** The wire form of an entry, rebuilt for storage. Only fields the client
 *  understands survive — which is exactly what it can act on. */
function entryToStorageJson(e: DirectoryEntry): Record<string, unknown> {
  return {
    key: e.entryKey,
    account_id: e.accountId,
    server_url: e.serverUrl,
    server_key: e.serverKey,
    status: e.status,
    // Local only, and never part of the published form: this client knows the
    // number because somebody typed it.
    ...(e.phone !== undefined ? { phone: e.phone } : {}),
    ...(e.successorKey !== undefined || e.successorAccountId !== undefined
      ? {
          successor: {
            ...(e.successorKey !== undefined ? { key: e.successorKey } : {}),
            ...(e.successorAccountId !== undefined ? { account_id: e.successorAccountId } : {}),
            ...(e.successorSigned ? { predecessor_signature: {} } : {}),
          },
        }
      : {}),
    ...(e.changePendingUntil !== undefined
      ? { change_pending_until: e.changePendingUntil.toISOString() }
      : {}),
    ...(e.minProtocolVersion !== undefined || e.maxProtocolVersion !== undefined
      ? {
          server_protocol_version: {
            ...(e.minProtocolVersion !== undefined ? { min: e.minProtocolVersion } : {}),
            ...(e.maxProtocolVersion !== undefined ? { max: e.maxProtocolVersion } : {}),
          },
        }
      : {}),
  };
}
